"use client";

import { FileText, Upload } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  ActionRow,
  ManuscriptFlags,
  MetadataResults,
  type ReferenceCheckStrings,
  Stat,
} from "@/components/tools/reference-check/results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  type BibliographyReport,
  buildBibliographyReport,
  identifiersForReferences,
  needsTitleLookup,
} from "@/lib/bibliography-report";
import type { VerifyResponse } from "@/lib/bibliography-verify";
import { checkReferenceList, splitEntries } from "@/lib/citation-check";
import { MAX_BATCH } from "@/lib/cite-service";
import { extractDocxText, extractReferences } from "@/lib/document-extract";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  lookupReferenceByTitle,
  type TitleLookupResult,
} from "@/lib/reference-title-lookup";

/**
 * English strings. A translated content file overrides these through `ui`.
 *
 * The same checker renders `/verify-bibliography` (`variant="audit"`, English-only,
 * which adds the APA formatting pass) and this tool (`variant="ai"`). The `sample` is a
 * reference list in the shape chatbots produce: a real work cited by title only, an
 * invented one with an unregistered DOI, a real one with its DOI, and a reference whose
 * DOI belongs to another paper. Real citations are not translated.
 */
export const FAKE_CITATION_CHECKER_STRINGS = {
  modeLegend: "What to check",
  modeReferences: "Reference list",
  modeManuscript: "Full manuscript",
  upload: "Upload .docx or .txt",
  inputReferences: "Reference list to check",
  inputManuscript: "Manuscript text",
  placeholderReferences:
    "Paste one reference per line, or separate wrapped references with a blank line…",
  placeholderManuscript:
    "Paste your manuscript, including its References or Bibliography section…",
  trySample: "Try a sample",
  sample:
    "Joly, J. F., Stapel, D. A., & Lindenberg, S. M. (2008). Silence and table manners: When environments activate norms. Personality and Social Psychology Bulletin, 34(8), 1047–1056.\nHartman, L. R., & Okafor, D. (2019). Neural correlates of bilingual lexical retrieval in adolescent sleep deprivation. Journal of Cognitive Neuroscience, 31(4), 512–529. https://doi.org/10.1162/jocn_a_99999\nWakefield, A. J., Murch, S. H., Anthony, A., et al. (1998). Ileal-lymphoid-nodular hyperplasia, non-specific colitis, and pervasive developmental disorder in children. The Lancet, 351(9103), 637–641. https://doi.org/10.1016/S0140-6736(97)11096-0\nSmith, J. (2021). Sleep and memory. Journal of Sleep, 12(3), 45–60. https://doi.org/10.1038/nature12373",
  checkReferences: "Check references",
  verifyReferences: "Verify references",
  progressIdentifiers: "Resolving identifiers…",
  progressTitles: "Searching titles: {done} of {total}…",
  limitNote: "Each check looks up the first {limit} references.",
  limitNoteAudit:
    "Each check looks up the first {limit} references; local checks cover all {count}.",
  fileTooLarge: "Choose a .docx or .txt file smaller than 10 MB.",
  fileUnreadable: "Could not read this file.",
  checkFailed:
    "The identifier lookup did not answer properly. Try again shortly.",
  resultsHeading: "Do these references exist?",
  privacyTitles:
    "Manuscript text stayed in this browser. Identifiers were sent to CitationEasy for lookup, and the titles of references that needed a search went straight from this browser to Crossref and OpenAlex.",
  privacyIdentifiers:
    "Reference and manuscript text stayed in this browser. Only the identifiers were sent for lookup.",
  privacyNone:
    "Reference and manuscript text stayed in this browser. No lookup was needed.",
  verdictVerified: "Found",
  verdictMismatch: "Details don't match",
  verdictNotFound: "No record found",
  verdictUnchecked: "Not checked",
  referenceNumber: "Reference {n}",
  sourceLabel: "Source:",
  provenanceDoi: "DOI registry",
  provenancePmid: "PubMed",
  provenanceIsbn: "Open Library",
  provenanceArxiv: "DataCite (arXiv DOI)",
  openProviderRecord: "Open the record",
  retrievedTitle: "Retrieved title:",
  retrievedAuthor: "Retrieved author:",
  retrievedDate: "Retrieved date:",
  findingYear:
    "The written year ({written}) differs from the retrieved record ({authoritative}). Check the edition or version before changing it.",
  findingTitle:
    "The title in this reference is substantially different from the retrieved record. Check that the identifier belongs to the work you meant to cite.",
  findingIncomplete:
    "The provider returned an incomplete record. Check the missing details by hand.",
  errorNoIdentifier:
    "No DOI, PMID, ISBN or arXiv ID was found, so this reference was not looked up.",
  errorUnresolved:
    "The identifier could not be resolved. That alone does not prove the source is invalid.",
  lookupRateLimited:
    "Crossref and OpenAlex are limiting requests from your network. Wait a minute and check again.",
  lookupUnavailable:
    "Crossref and OpenAlex did not answer properly, so this title was not searched. Try again shortly.",
  lookupNetwork:
    "Crossref and OpenAlex could not be reached, so this title was not searched. Check your connection.",
  bothIndexes: "Crossref and OpenAlex",
  noMatch:
    "No work with this title was found in {sources}. Fabricated references look like this, but so do many books, reports, web pages and regional journals: search a library catalogue or Google Scholar before you delete it.",
  headingFound: "Matching record in {source}",
  headingFoundElsewhere:
    "A work with this title is in {source}, under a different identifier",
  headingClose: "Similar record in {source} — compare it with your reference",
  headingNearest: "No match in {sources}. Nearest record found",
  etAl: "et al.",
  differences: "Differences: {list}.",
  differenceTitle: "the title differs",
  differenceYear: "the year differs by more than one",
  differenceAuthor: "none of the first authors is named in your reference",
  openRecord: "Open record",
  skipped:
    "one: {count} more reference was not looked up. Check it in a second pass.|other: {count} more references were not looked up. Check them in a second pass.",
  caveat:
    "“Found” means an identifier resolved to the same work, or a record with the same title, authors and year exists in Crossref or OpenAlex. It does not mean the source says what you cite it for, is unretracted, or that every citation detail is correct. “No record found” is a reason to look harder, not proof that a source was invented.",
  crossCheckHeading: "In-text citation cross-check",
  crossCheckClean:
    "No author–date mismatches were found. This check matches first author and year, so review ambiguous names by hand.",
  missingFromList: "Cited in the manuscript but missing from the list",
  neverCited: "In the reference list but not found in the manuscript",
  localChecksHeading: "Local integrity checks",
  statReferences: "References",
  statApaFlags: "APA format flags",
  statInText: "In-text citations",
  statCrossCheck: "Cross-check flags",
  apaFindingsHeading: "APA formatting findings to review",
  apaFindingItem: "Reference {n}: {title}",
} as const;

/** `/verify-bibliography`'s sample: a short manuscript, so the cross-check has work. */
const AUDIT_SAMPLE = `Introduction

Sleep loss impairs recall (Smith, 2020), while a later study found different effects (Walker, 2019).

References

Smith, J. (2021). Sleep and memory. Journal of Sleep, 12(3), 45–60. https://doi.org/10.1038/nature12373
Walker, M. (2019). Why we sleep. Scribner. ISBN: 9781501144318`;

const MAX_UPLOAD_BYTES = 10_000_000;
/** References sent through the identifier and title lookups in one check. */
const LOOKUP_LIMIT = 20;
/** Keeps title searches well inside Crossref's and OpenAlex's rate limits. */
const TITLE_SPACING_MS = 300;
type Mode = "references" | "manuscript";
export type VerifyVariant = "audit" | "ai";

class LookupError extends Error {}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Resolve identifiers through `/api/v1/verify`, ten to a request. */
async function resolveIdentifiers(
  identifiers: string[]
): Promise<VerifyResponse> {
  const merged: VerifyResponse = {
    summary: { identifiers: 0, matched: 0, unresolved: 0 },
    privacy:
      "This endpoint accepts identifiers only, not reference or manuscript prose.",
    results: [],
  };
  for (let start = 0; start < identifiers.length; start += MAX_BATCH) {
    const response = await fetch("/api/v1/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        identifiers: identifiers.slice(start, start + MAX_BATCH),
      }),
    });
    const data = (await response
      .json()
      .catch(() => null)) as VerifyResponse | null;
    if (!(response.ok && data)) {
      throw new LookupError();
    }
    merged.results.push(...data.results);
    merged.summary.identifiers += data.summary.identifiers;
    merged.summary.matched += data.summary.matched;
    merged.summary.unresolved += data.summary.unresolved;
  }
  return merged;
}

function LocalChecks({
  references,
  strings,
  fmt,
  inText,
  crossCheckFlags,
}: {
  references: string[];
  strings: ReferenceCheckStrings;
  fmt: (value: number) => string;
  inText: number;
  crossCheckFlags: number;
}) {
  const styleReport = checkReferenceList(references.join("\n"));
  return (
    <section className="space-y-4">
      <h2 className="font-semibold text-xl">{strings.localChecksHeading}</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={strings.statReferences}
          value={fmt(styleReport.totalEntries)}
        />
        <Stat
          label={strings.statApaFlags}
          value={fmt(styleReport.totalFindings)}
        />
        <Stat label={strings.statInText} value={fmt(inText)} />
        <Stat label={strings.statCrossCheck} value={fmt(crossCheckFlags)} />
      </div>
      {styleReport.totalFindings > 0 ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <p className="font-medium text-sm">{strings.apaFindingsHeading}</p>
          <ul className="mt-2 list-disc space-y-1 ps-5 text-muted-foreground text-sm">
            {styleReport.entries.flatMap((entry) =>
              entry.findings.map((finding) => (
                <li key={`${entry.position}-${finding.rule}`}>
                  {t(strings.apaFindingItem, {
                    n: fmt(entry.position),
                    title: finding.title,
                  })}
                </li>
              ))
            )}
            {styleReport.listFindings.map((finding) => (
              <li key={finding.rule}>{finding.title}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

/** The checker behind both `/verify-bibliography` and the Fake Citation Checker. */
export function ReferenceChecker({ variant }: { variant: VerifyVariant }) {
  const strings: ReferenceCheckStrings = useToolUi(
    FAKE_CITATION_CHECKER_STRINGS
  );
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("references");
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [report, setReport] = useState<BibliographyReport | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const extraction = useMemo(
    () =>
      mode === "manuscript" && text.trim() ? extractReferences(text) : null,
    [mode, text]
  );
  const references = useMemo(
    () => extraction?.entries ?? splitEntries(text),
    [extraction, text]
  );
  const skipped = Math.max(0, references.length - LOOKUP_LIMIT);

  async function handleFile(file: File) {
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      if (file.size > MAX_UPLOAD_BYTES) {
        setError(strings.fileTooLarge);
        return;
      }
      const contents = file.name.toLowerCase().endsWith(".docx")
        ? await extractDocxText(await file.arrayBuffer())
        : await file.text();
      setText(contents);
      setMode("manuscript");
      setFileName(file.name);
    } catch {
      setError(strings.fileUnreadable);
    } finally {
      setBusy(false);
    }
  }

  async function searchTitles(
    checkedReferences: string[],
    preliminary: BibliographyReport
  ): Promise<Map<number, TitleLookupResult>> {
    const pending = preliminary.results.filter((result) =>
      needsTitleLookup(result.status)
    );
    const lookups = new Map<number, TitleLookupResult>();
    for (const [index, result] of pending.entries()) {
      setProgress(
        t(strings.progressTitles, {
          done: fmt(index + 1),
          total: fmt(pending.length),
        })
      );
      if (index > 0) {
        await wait(TITLE_SPACING_MS);
      }
      lookups.set(
        result.position,
        await lookupReferenceByTitle(checkedReferences[result.position - 1])
      );
    }
    return lookups;
  }

  async function check() {
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const checkedReferences = references.slice(0, LOOKUP_LIMIT);
      setProgress(strings.progressIdentifiers);
      const identifierResponse = await resolveIdentifiers(
        identifiersForReferences(checkedReferences)
      );
      const preliminary = buildBibliographyReport(
        checkedReferences,
        identifierResponse
      );
      const titleLookups = await searchTitles(checkedReferences, preliminary);
      setReport(
        buildBibliographyReport(
          checkedReferences,
          identifierResponse,
          titleLookups
        )
      );
    } catch {
      setError(strings.checkFailed);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  let limitNote: string | null = null;
  if (skipped > 0) {
    limitNote = t(
      variant === "ai" ? strings.limitNote : strings.limitNoteAudit,
      { limit: fmt(LOOKUP_LIMIT), count: fmt(references.length) }
    );
  }

  return (
    <div className="space-y-8">
      <fieldset className="flex flex-wrap gap-2">
        <legend className="sr-only">{strings.modeLegend}</legend>
        {(
          [
            ["references", strings.modeReferences],
            ["manuscript", strings.modeManuscript],
          ] as const
        ).map(([value, label]) => (
          <button
            aria-pressed={mode === value}
            className={`rounded-lg px-4 py-2 font-medium text-sm ${
              mode === value
                ? "bg-primary text-primary-foreground"
                : "border border-border bg-card text-foreground"
            }`}
            key={value}
            onClick={() => {
              setMode(value);
              setReport(null);
            }}
            type="button"
          >
            {label}
          </button>
        ))}
      </fieldset>

      {mode === "manuscript" ? (
        <div className="flex flex-wrap items-center gap-3">
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 font-medium text-sm"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
            type="button"
          >
            <Upload className="size-4" />
            {strings.upload}
          </button>
          <input
            accept=".docx,.txt,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                handleFile(file);
              }
              event.target.value = "";
            }}
            ref={fileInput}
            type="file"
          />
          {fileName ? (
            <span className="inline-flex items-center gap-2 text-muted-foreground text-sm">
              <FileText className="size-4" />
              {fileName}
            </span>
          ) : null}
        </div>
      ) : null}

      <textarea
        aria-label={
          mode === "manuscript"
            ? strings.inputManuscript
            : strings.inputReferences
        }
        className="min-h-[240px] w-full resize-y rounded-lg border border-border bg-card p-4 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(event) => {
          setText(event.target.value);
          setFileName(null);
          setReport(null);
        }}
        placeholder={
          mode === "manuscript"
            ? strings.placeholderManuscript
            : strings.placeholderReferences
        }
        value={text}
      />

      <ActionRow
        busy={busy}
        limitNote={limitNote}
        onSample={() => {
          setText(variant === "ai" ? strings.sample : AUDIT_SAMPLE);
          setMode(variant === "ai" ? "references" : "manuscript");
          setReport(null);
        }}
        onVerify={check}
        progress={progress}
        referenceCount={references.length}
        strings={strings}
        variant={variant}
      />

      {error ? (
        <p
          className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-destructive text-sm"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      {report ? (
        <MetadataResults
          fmt={fmt}
          report={report}
          skippedNote={
            skipped > 0
              ? tPlural(strings.skipped, locale, skipped, {
                  count: fmt(skipped),
                })
              : null
          }
          strings={strings}
        />
      ) : null}

      {references.length > 0 && variant === "audit" ? (
        <LocalChecks
          crossCheckFlags={
            (extraction?.missingFromList.length ?? 0) +
            (extraction?.neverCited.length ?? 0)
          }
          fmt={fmt}
          inText={extraction?.inTextCitations.length ?? 0}
          references={references}
          strings={strings}
        />
      ) : null}

      {extraction ? (
        <ManuscriptFlags extraction={extraction} strings={strings} />
      ) : null}
    </div>
  );
}

export default function FakeCitationCheckerWidget() {
  return <ReferenceChecker variant="ai" />;
}
